import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { jwtDecode } from 'jwt-decode';
import { Observable, of } from 'rxjs';
import { ApiHandlerService } from '../../../shared/services/api-handler/api-handler.service';
import { AUTHX_APP_ID } from '../../../core/constants/permissions.constants';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly jwtTokenKey = 'token';
  private readonly refreshTokenKey = 'refreshToken';

  constructor(private router: Router, private apiHandler: ApiHandlerService) { }

  extractDetailsFromJWT(token: string): {
    user_id: string;
    session_id: string;
    first_name: string;
  } {
    interface JwtPayload {
      user_id: string;
      session_id: string;
      first_name: string;
      last_name: string;
    }

    try {
      const decodedJwt: JwtPayload = jwtDecode<JwtPayload>(token);

      if (!decodedJwt.user_id || !decodedJwt.session_id) {
        throw new Error('Invalid token: Missing required properties');
      }

      return {
        user_id: decodedJwt.user_id,
        session_id: decodedJwt.session_id,
        first_name: decodedJwt.first_name,
      };
    } catch (error) {
      console.error('Error decoding JWT:', error);
      throw new Error('Invalid token');
    }
  }

  loginUser(formData: FormGroup): Observable<any> {
    return this.apiHandler.fetchData(
      '/orchestrate/v1/login',
      'POST',
      formData,
      false,
      0,
      false
    );
  }

  isAuthenticated(): boolean {
    const token = localStorage.getItem(this.jwtTokenKey);
    const refreshToken = localStorage.getItem(this.refreshTokenKey);

    if (!token || !refreshToken) {
      return false;
    }

    try {
      const decodedToken: any = jwtDecode(token);

      if (!decodedToken.exp) {
        console.error('Token does not contain expiration time');
        return false;
      }

      const currentTime = Math.floor(Date.now() / 1000);

      if (decodedToken.exp > currentTime) {
        return true;
      }

      // Token expired, attempt to refresh
      this.apiHandler.renewAccessToken(refreshToken).subscribe({
        next: (response) => {
          if (response.accessToken) {
            localStorage.setItem(this.jwtTokenKey, response.accessToken);
          }
        },
        error: () => {
          return false;
        }
      });

      return true;
    } catch (error) {
      console.error('Error decoding token:', error);
      return false;
    }
  }

  getJWTToken(): string {
    return localStorage.getItem(this.jwtTokenKey)!;
  }

  getUserPermissions(userId: string, version: string): Observable<any> {
    return this.apiHandler.fetchData(
      `/authX/v1/user/${userId}/permissions/${version}`,
      'GET',
    );
  }

  getUserInfo(): Observable<any> {
    return this.apiHandler.fetchData(
      `/orchestrate/v1/user-info?appId=9`,
      'GET',
      undefined,
      false,
      0,
      false
    );
  }

  getPermissions(): Observable<any> {
    try {
      return this.apiHandler.fetchData(
        `/authX/v1/permissions/app/${AUTHX_APP_ID}?version=1`,
        'GET',
      );
    } catch (error) {
      console.error('Error fetching permissions:', error);
      return of(null);
    }
  }

  // logout(): void {
  //   localStorage.removeItem(this.jwtTokenKey);
  //   this.router.navigate(['/login']);
  // }

  // getToken(): string {
  //   return localStorage.getItem(this.jwtTokenKey)!;
  // }
}
